import React, { useEffect, useRef, useState } from "react";
import { CalendarIcon, ChevronDown, Plane, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import CustomDatePicker from "../../../../components/shared/date-picker/Date/CustomDatePicker";
import { formatDateInput } from "../../utils/analyticsFormat";
import styles from "../../OrderAnalyticsPage.module.scss";

const modes = [
  { key: "all", label: "Все" },
  { key: "auto", label: "Авто", icon: Truck },
  { key: "air", label: "Авиа", icon: Plane },
];

const presets = [
  { days: 7, label: "Последние 7 дней" },
  { days: 30, label: "Последние 30 дней" },
  { days: 90, label: "Последние 90 дней" },
];

function dateFromInput(value) {
  return value ? new Date(`${value}T00:00:00`) : null;
}

function presetPeriod(days) {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - days + 1);
  return { startDate: formatDateInput(start), endDate: formatDateInput(end) };
}

function samePeriod(first, second) {
  return first.startDate === second.startDate && first.endDate === second.endDate;
}

function shortDate(value) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
}

export function TransportModeFilter({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div
      className={styles.modeFilter}
      role="radiogroup"
      aria-label={t("analyticsNew.transportMode", "Вид перевозки")}
    >
      {modes.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={value === key}
          className={value === key ? styles.selected : ""}
          onClick={() => onChange(key)}
        >
          {Icon && <Icon size={15} aria-hidden="true" />}
          {t(`analyticsNew.mode.${key}`, label)}
        </button>
      ))}
    </div>
  );
}

export function PeriodFilter({ period, setPeriod, validPeriod }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const startDate = dateFromInput(period.startDate);
  const endDate = dateFromInput(period.endDate);
  const activePreset = presets.find((preset) => samePeriod(period, presetPeriod(preset.days)));

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const applyPreset = (preset) => {
    setPeriod(presetPeriod(preset.days));
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className={styles.periodFilter} ref={rootRef}>
      <button
        type="button"
        className={styles.periodTrigger}
        ref={triggerRef}
        aria-label={t("analyticsNew.period", "Период")}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <CalendarIcon size={15} aria-hidden="true" />
        <span>
          {activePreset
            ? t(`analyticsNew.periodPreset.${activePreset.days}`, activePreset.label)
            : `${shortDate(period.startDate)} — ${shortDate(period.endDate)}`}
        </span>
        <ChevronDown className={open ? styles.chevronOpen : ""} size={14} aria-hidden="true" />
      </button>
      {open && (
        <div
          className={styles.periodPopover}
          role="dialog"
          aria-label={t("analyticsNew.periodPicker", "Выбор периода")}
        >
          <div className={styles.periodPresets}>
            {presets.map((preset) => {
              const presetValue = presetPeriod(preset.days);
              const selected = samePeriod(period, presetValue);
              return (
                <button
                  type="button"
                  className={selected ? styles.selected : ""}
                  key={preset.days}
                  onClick={() => applyPreset(preset)}
                >
                  <span>{t(`analyticsNew.periodPreset.${preset.days}`, preset.label)}</span>
                  <span className={styles.presetRange}>
                    {shortDate(presetValue.startDate)} — {shortDate(presetValue.endDate)}
                  </span>
                </button>
              );
            })}
          </div>
          <div className={styles.customPeriod}>
            <div className={styles.customPeriodTitle}>
              {t("analyticsNew.customPeriod", "Свой период")}
            </div>
            <div className={styles.customPeriodRow}>
              <div className={styles.customDateField}>
                <label className={styles.srOnly} htmlFor="analytics-start">
                  {t("analyticsNew.startDate", "Начало периода")}
                </label>
                <span aria-hidden="true">{t("analyticsNew.from", "С")}</span>
                <CustomDatePicker
                  id="analytics-start"
                  selected={startDate}
                  maxDate={endDate}
                  withBorder
                  showYearNavigation
                  strictParsing
                  ariaInvalid={!validPeriod ? "true" : undefined}
                  ariaDescribedBy={!validPeriod ? "analytics-date-popover-error" : undefined}
                  onChange={(date) =>
                    setPeriod((previous) => ({
                      ...previous,
                      startDate: date ? formatDateInput(date) : "",
                    }))
                  }
                />
              </div>
              <div className={styles.customDateField}>
                <label className={styles.srOnly} htmlFor="analytics-end">
                  {t("analyticsNew.endDate", "Конец периода")}
                </label>
                <span aria-hidden="true">{t("analyticsNew.to", "По")}</span>
                <CustomDatePicker
                  id="analytics-end"
                  selected={endDate}
                  minDate={startDate}
                  withBorder
                  showYearNavigation
                  strictParsing
                  ariaInvalid={!validPeriod ? "true" : undefined}
                  ariaDescribedBy={!validPeriod ? "analytics-date-popover-error" : undefined}
                  onChange={(date) =>
                    setPeriod((previous) => ({
                      ...previous,
                      endDate: date ? formatDateInput(date) : "",
                    }))
                  }
                />
              </div>
            </div>
            {!validPeriod && (
              <div className={styles.periodError} id="analytics-date-popover-error" role="alert">
                {!period.startDate || !period.endDate
                  ? t("analyticsNew.requiredDates", "Укажите начало и конец периода")
                  : t("analyticsNew.invalidDates", "Начало периода не может быть позже конца")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
